import { useRef, useState } from "react";
import { useIntl } from "react-intl";

import { IconButton } from "@/components/IconButton/IconButton";
import {
  CaretLeftIcon,
  CaretRightIcon,
} from "@/components/Icons/rtlReadyIcons";
import { LazyImage } from "@/components/Image/LazyImage";
import {
  ImageCarousel,
  type ImageCarouselHandle,
} from "@/components/ImageCarousel/ImageCarousel";
import type { ImageData } from "@/types/types";

type HotelRoomsImageCarouselProps = {
  images: ImageData[];
};

export const HotelRoomsImageCarousel = ({
  images,
}: HotelRoomsImageCarouselProps) => {
  const intl = useIntl();
  const sliderRef = useRef<ImageCarouselHandle>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const changeSlide = (index: number) => {
    sliderRef.current?.instance?.moveToIdx(index);
  };

  return (
    <div className="relative w-full overflow-hidden rounded-t-3xl">
      <ImageCarousel
        sliderRef={sliderRef}
        items={images}
        loop={false}
        getItemKey={({ url }) => url}
        onSlideChange={setCurrentIndex}
        className="aspect-[358/210]"
        slideClassName="h-full"
        slides={{ perView: 1, spacing: 0, origin: "auto" }}
        renderItem={({ url, alt }) => (
          <LazyImage
            src={url}
            alt={alt}
            className="h-full w-full object-cover"
            rootMargin="0px 100px 0px 100px"
          />
        )}
      />

      {images.length > 1 && (
        <>
          {currentIndex > 0 && (
            <div className="absolute start-3 top-1/2 z-50 -translate-y-1/2 rounded-full bg-black/45">
              <IconButton
                onClick={() => changeSlide(currentIndex - 1)}
                aria-label={intl.formatMessage({
                  defaultMessage: "Previous image",
                  description:
                    "ARIA label for button to navigate to previous image in gallery",
                })}
              >
                <CaretLeftIcon
                  size={16}
                  weight="bold"
                  color="var(--color-white)"
                />
              </IconButton>
            </div>
          )}
          {currentIndex < images.length - 1 && (
            <div className="absolute end-3 top-1/2 z-50 -translate-y-1/2 rounded-full bg-black/45">
              <IconButton
                onClick={() => changeSlide(currentIndex + 1)}
                aria-label={intl.formatMessage({
                  defaultMessage: "Next image",
                  description:
                    "ARIA label for button to navigate to next image in gallery",
                })}
              >
                <CaretRightIcon
                  size={16}
                  weight="bold"
                  color="var(--color-white)"
                />
              </IconButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};
